import { GAME_DOCS_PATH, LoginFromGame, PlayerRouteSingleGameIframe } from "@app/app/routing/paths.ts";
import { useRequiredPortalAndGameConfigContext } from "@entities/game/model/useConfigContext.ts";
import { useFirebaseAuth } from "@features/authV2/lib/firebase.ts";
import { DocumentView } from "@features/documents/ui/DocumentView.tsx";
import { Center, Loader } from "@mantine/core";
import log from "loglevel";
import React, { Suspense, useEffect } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { DiscordQr } from "src/pages/DiscordQrPage";
import { LoginFromFxEmail } from "src/pages/LoginFromEmailPage";
import { LoginWithTempToken } from "src/pages/LoginWithTempTokenPage";

const WebPlayer = React.lazy(() => import("@app/pages/WebPlayerPage/WebPlayer.tsx"));
const LoginFromGamePage = React.lazy(() => import("@pages/LoginFromGamePage"));
const DocsScreen = React.lazy(() => import("@app/pages/DocsPage"));
const StoreReturnScreen = React.lazy(() => import("@app/pages/StorePage/Components/StoreReturnScreen"));
const StoreContinueWithPI = React.lazy(() => import("@app/pages/StorePage/Components/StoreContinueWithPI"));
const HeroSmallLayout = React.lazy(() => import("@widgets/HeroLayout/HeroSmallLayout"));
const DebugPage = React.lazy(() => import("@app/pages/DebugPage"));
const MerchantPaymentCompleted = React.lazy(() => import("@app/pages/StorePage/Components/MerchantPaymentCompleted"));
/**
 * @fileoverview Роуты для режима "сайт одной игры".
 */

const FullscreenFallback = () => (
	<Center h="100vh">
		<Loader size="xl" />
	</Center>
);

const SingleGameSiteRoutes = () => {
	const { gameConfig, portalConfig } = useRequiredPortalAndGameConfigContext();

	const firebaseConfig =
		gameConfig?.PublicWebClientConfig.Firebase ??
		portalConfig.PortalPublicWebConfig.CompanyBranding.FirebaseOptions;

	useFirebaseAuth(firebaseConfig);

	useEffect(() => {
		log.info("Using single-game site routes for game %o", gameConfig.GameSystemName);
	}, []);

	return (
		<Routes>
			<Route
				element={
					<Suspense fallback={<FullscreenFallback />}>
						<Outlet />
					</Suspense>
				}
			>
				<Route path="/play">
					<Route path=":game" element={<WebPlayer singleGameSite={false} />} />
					<Route path=":game/iframe/:adapter" element={<WebPlayer singleGameSite={false} />} />
				</Route>
				<Route path="/player">
					<Route path=":game" element={<WebPlayer singleGameSite={false} />} />
					<Route path=":game/iframe/:adapter" element={<WebPlayer singleGameSite={false} />} />
				</Route>

				<Route path=":game/documents/:id/view" element={<DocumentView/>}/>

				<Route path="/" element={<WebPlayer singleGameSite />} />
				<Route path={PlayerRouteSingleGameIframe} element={<WebPlayer singleGameSite />} />
				<Route path="Store/Return/:id" element={<StoreReturnScreen />} />
				<Route path="Store/MerchantPaymentCompleted/:token" element={<MerchantPaymentCompleted />} />
				<Route path="LoginValidateEmail" element={<LoginFromFxEmail />} />
				<Route path={LoginFromGame} element={<LoginFromGamePage />} />
				<Route path="LoginWithTempToken" element={<LoginWithTempToken />} />
				<Route
					path="/Store/ContinueWithPI"
					element={<HeroSmallLayout portalConfig={portalConfig} gameConfig={gameConfig} />}
				>
					<Route index element={<StoreContinueWithPI />} />
				</Route>
				<Route path="/discord-qr" element={<DiscordQr />} />
				<Route path="Docs/:doc" element={<DocsScreen portalConfig={portalConfig} />} />
				{/* Для обратной совместимости, если в URL есть адаптер или игра */}
				<Route path={GAME_DOCS_PATH} element={<DocsScreen portalConfig={portalConfig} />} />
				{import.meta.env.DEV && <Route path="/debug" element={<DebugPage />} />}
			</Route>
			<Route path="*" element={<Navigate to="/" replace />} />
		</Routes>
	);
};

export default SingleGameSiteRoutes;
